/**
 * CartJS — cross-store cart persistence via SKU-encoded `?PerC=` handoff links.
 *
 * Encode: GET /cart.js → compact SKU triples → base64url
 * Decode: look up assets/cartjs-sku-map.json → clear cart → /cart/add.js
 *
 * `?PerC=` is only attached on intentional Deliver To navigations, then stripped
 * after restore. Same-store persistence uses Shopify's cart cookie — we do not
 * rewrite the address bar on every cart change (that fragments cache/analytics).
 *
 * Selling-plan numeric IDs are store-specific; the code carries plan *names*
 * (and NRO properties). Destination resolves name → local selling_plan_id.
 * `_nro_uid` is regenerated on restore and never encoded.
 */

const PARAM = 'PerC';
/** Legacy query key; still accepted on inbound handoffs. */
const LEGACY_PARAM = 'cartjs';
const MAP_META_ID = 'cartjs-sku-map-url';
const NOTICE_ID = 'cartjs-restore-notice';

/**
 * @typedef {{
 *   sku: string,
 *   quantity: number,
 *   planName: string,
 *   usage: string,
 *   cycleDays: string,
 *   cartonCount: string,
 * }} CartJsLine
 */

/**
 * @returns {Record<string, any>}
 */
function themeConfig() {
  return /** @type {Record<string, any>} */ (window.Theme || {});
}

/**
 * @param {string} key
 * @param {Record<string, string|number>} [vars]
 */
function t(key, vars = {}) {
  const translations = themeConfig().translations || {};
  let text = translations[key] || fallbackTranslations[key] || key;
  for (const [k, v] of Object.entries(vars)) {
    text = text.replaceAll(`{{ ${k} }}`, String(v));
  }
  return text;
}

const fallbackTranslations = {
  cartjs_restored_all: 'We moved {{ moved }} of {{ total }} items to this store.',
  cartjs_restored_partial:
    'We moved {{ moved }} of {{ total }} items to this store. Some items aren’t available here.',
  cartjs_restored_none: 'None of the items in that cart are available on this store.',
  cartjs_restore_error: 'We couldn’t restore that cart. Please try again.',
};

/**
 * @param {Uint8Array} bytes
 */
function bytesToBase64Url(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

/**
 * @param {string} value
 */
function base64UrlToBytes(value) {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (padded.length % 4)) % 4;
  const binary = atob(padded + '='.repeat(padLen));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * @param {string} text
 */
function encodeCode(text) {
  return bytesToBase64Url(new TextEncoder().encode(text));
}

/**
 * @param {string} code
 */
function decodeCode(code) {
  return new TextDecoder().decode(base64UrlToBytes(code));
}

/**
 * @param {CartJsLine[]} lines
 */
export function encodeCartLines(lines) {
  const payload = lines
    .map((line) =>
      [
        line.sku,
        String(line.quantity || 1),
        line.planName || '',
        line.usage || '',
        line.cycleDays || '',
        line.cartonCount || '',
      ]
        .map((part) => encodeURIComponent(part))
        .join(':')
    )
    .join(';');
  return encodeCode(payload);
}

/**
 * @param {string} code
 * @returns {CartJsLine[]}
 */
export function decodeCartCode(code) {
  const text = decodeCode(code.trim());
  if (!text) return [];
  return text
    .split(';')
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => {
      const parts = chunk.split(':').map((p) => {
        try {
          return decodeURIComponent(p);
        } catch {
          return p;
        }
      });
      const [sku = '', qty = '1', planName = '', usage = '', cycleDays = '', cartonCount = ''] = parts;
      return {
        sku: sku.trim(),
        quantity: Math.max(1, Number.parseInt(qty, 10) || 1),
        planName: planName.trim(),
        usage: usage.trim(),
        cycleDays: cycleDays.trim(),
        cartonCount: cartonCount.trim(),
      };
    })
    .filter((line) => line.sku);
}

/**
 * @param {any} cart
 * @returns {CartJsLine[]}
 */
export function cartToLines(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  /** @type {CartJsLine[]} */
  const lines = [];
  for (const item of items) {
    const sku = String(item?.sku || '').trim();
    if (!sku) continue;
    const props = item?.properties || {};
    const planName = String(item?.selling_plan_allocation?.selling_plan?.name || '').trim();
    lines.push({
      sku,
      quantity: Math.max(1, Number(item?.quantity) || 1),
      planName,
      usage: String(props._nro_usage || props['_nro_usage'] || '').trim(),
      cycleDays: String(props._nro_cycle_days || props['_nro_cycle_days'] || '').trim(),
      cartonCount: String(props._nro_carton_count || props['_nro_carton_count'] || '').trim(),
    });
  }
  return lines;
}

/**
 * Build a Deliver To handoff URL with `?PerC=` (only used at region switch).
 * @param {CartJsLine[]} lines
 * @param {string} [baseUrl]
 */
export function buildShareUrl(lines, baseUrl = window.location.href) {
  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.delete(LEGACY_PARAM);
  if (lines.length === 0) {
    url.searchParams.delete(PARAM);
  } else {
    url.searchParams.set(PARAM, encodeCartLines(lines));
  }
  return url.toString();
}

/**
 * @returns {Promise<CartJsLine[]>}
 */
export async function readCartLines() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`cart.js ${response.status}`);
  const cart = await response.json();
  return cartToLines(cart);
}

/**
 * @returns {Promise<string>}
 */
export async function buildShareUrlFromCart(baseUrl) {
  const lines = await readCartLines();
  return buildShareUrl(lines, baseUrl);
}

/**
 * @returns {string|null}
 */
function mapUrlFromPage() {
  const meta = document.getElementById(MAP_META_ID);
  const fromMeta = meta?.getAttribute('content') || meta?.textContent?.trim();
  if (fromMeta) return fromMeta;
  const fromTheme = themeConfig().cartjs?.skuMapUrl;
  return fromTheme || null;
}

/**
 * @returns {Promise<{ skus: Record<string, any>, shop?: string }>}
 */
async function loadSkuMap() {
  const url = mapUrlFromPage();
  if (!url) throw new Error('Missing cartjs SKU map URL');
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`sku map ${response.status}`);
  return response.json();
}

/**
 * @param {Record<string, number>} plans
 * @param {string} planName
 * @param {string} cycleDays
 * @returns {number|null}
 */
function resolveSellingPlanId(plans, planName, cycleDays) {
  if (!plans || typeof plans !== 'object') return null;
  if (planName && plans[planName]) return plans[planName];

  if (planName) {
    const lower = planName.toLowerCase();
    for (const [name, id] of Object.entries(plans)) {
      if (name.toLowerCase() === lower) return /** @type {number} */ (id);
    }
  }

  const days = Number.parseInt(cycleDays, 10);
  if (Number.isFinite(days) && days > 0) {
    const re = new RegExp(`(?:^|\\D)${days}\\s*day`, 'i');
    for (const [name, id] of Object.entries(plans)) {
      if (re.test(name)) return /** @type {number} */ (id);
    }
  }

  return null;
}

/**
 * @returns {string}
 */
function newNroUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {string} message
 * @param {'success'|'partial'|'error'} tone
 */
function showNotice(message, tone) {
  let el = document.getElementById(NOTICE_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = NOTICE_ID;
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.style.cssText = [
      'position:fixed',
      'z-index:9999',
      'left:50%',
      'bottom:1.25rem',
      'transform:translateX(-50%)',
      'max-width:min(32rem,calc(100vw - 2rem))',
      'padding:0.85rem 1.1rem',
      'border-radius:0.5rem',
      'font:500 0.95rem/1.35 system-ui,sans-serif',
      'box-shadow:0 8px 24px rgba(0,0,0,.18)',
      'color:#111',
      'background:#fff',
      'border:1px solid #ddd',
    ].join(';');
    document.body.appendChild(el);
  }

  if (tone === 'error') el.style.borderColor = '#c0392b';
  else if (tone === 'partial') el.style.borderColor = '#d68910';
  else el.style.borderColor = '#1e8449';

  el.textContent = message;
  window.setTimeout(() => {
    el?.remove();
  }, 8000);
}

function stripCartJsParam() {
  const url = new URL(window.location.href);
  const hadParam = url.searchParams.has(PARAM) || url.searchParams.has(LEGACY_PARAM);
  if (!hadParam) return;
  url.searchParams.delete(PARAM);
  url.searchParams.delete(LEGACY_PARAM);
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', next);
}

/**
 * Show View Cart pill with spinner in the count badge during restore.
 * @param {boolean} loading
 */
function setCartIconRestoring(loading) {
  const icons = document.querySelectorAll('cart-icon');
  for (const icon of icons) {
    if (typeof /** @type {any} */ (icon).setRestoring === 'function') {
      /** @type {any} */ (icon).setRestoring(loading);
    }
  }
}

/**
 * @param {any} data
 */
function dispatchCartUpdate(data) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: data,
        sourceId: 'cartjs',
        data: {
          source: 'cartjs',
          itemCount: data?.item_count,
        },
      },
    })
  );
}

/**
 * @param {CartJsLine} line
 * @param {any} entry
 */
function buildAddPayload(line, entry) {
  /** @type {Record<string, any>} */
  const payload = {
    id: entry.variant_id,
    quantity: line.quantity,
  };

  const planId = resolveSellingPlanId(entry.selling_plans || {}, line.planName, line.cycleDays);
  if (planId) {
    payload.selling_plan = planId;
    /** @type {Record<string, string>} */
    const properties = { _nro_uid: newNroUid() };
    if (line.usage) properties._nro_usage = line.usage;
    if (line.cycleDays) properties._nro_cycle_days = line.cycleDays;
    if (line.cartonCount) properties._nro_carton_count = line.cartonCount;
    payload.properties = properties;
  }

  return payload;
}

/**
 * @param {string} addUrl
 * @param {Record<string, any>} payload
 */
async function addSingleItem(addUrl, payload) {
  const response = await fetch(addUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });
  return response.ok;
}

/**
 * @param {CartJsLine[]} lines
 * @param {{ skus: Record<string, any> }} map
 */
function resolveMapEntry(map, sku) {
  const key = String(sku || '')
    .trim()
    .toUpperCase();
  if (!key || !map?.skus) return null;
  if (map.skus[key]) return map.skus[key];

  // USA catalogs often insert a "B" before an optional multipack suffix:
  // PVSS15 ↔ PVSS15B, DVPSM30X6 ↔ DVPSM30BX6
  const withoutB = key.replace(/B(?=X\d+$)/, '').replace(/B$/, '');
  if (withoutB !== key && map.skus[withoutB]) return map.skus[withoutB];

  const withB = /B(X\d+)?$/.test(key) ? key : key.replace(/(X\d+)?$/, (m) => `B${m || ''}`);
  if (withB !== key && map.skus[withB]) return map.skus[withB];

  // Generator may attach explicit aliases (pack-count mismatches, etc.).
  const aliasTarget = map.aliases?.[key];
  if (aliasTarget && map.skus[aliasTarget]) return map.skus[aliasTarget];

  return null;
}

/**
 * @param {CartJsLine[]} lines
 * @param {{ skus: Record<string, any>, aliases?: Record<string, string> }} map
 */
async function restoreLines(lines, map) {
  const addUrl = themeConfig().routes?.cart_add_url || '/cart/add.js';

  // Replace destination cart (recovery / cross-store handoff).
  await fetch('/cart/clear.js', {
    method: 'POST',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  /** @type {{ line: CartJsLine, payload: Record<string, any> }[]} */
  const resolvable = [];
  /** @type {string[]} */
  const skipped = [];

  for (const line of lines) {
    const entry = resolveMapEntry(map, line.sku);
    if (!entry?.variant_id) {
      skipped.push(line.sku);
      continue;
    }
    resolvable.push({ line, payload: buildAddPayload(line, entry) });
  }

  let moved = 0;

  if (resolvable.length > 0) {
    // Prefer one batched /cart/add.js call (much faster than sequential adds).
    try {
      const batchResponse = await fetch(addUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items: resolvable.map((r) => r.payload) }),
      });

      if (batchResponse.ok) {
        moved = resolvable.length;
      } else {
        // Fall back to parallel individual adds if batch is rejected.
        const results = await Promise.all(
          resolvable.map(({ line, payload }) =>
            addSingleItem(addUrl, payload).then((ok) => ({ sku: line.sku, ok }))
          )
        );
        for (const result of results) {
          if (result.ok) moved += 1;
          else skipped.push(result.sku);
        }
      }
    } catch {
      const results = await Promise.all(
        resolvable.map(({ line, payload }) =>
          addSingleItem(addUrl, payload).then((ok) => ({ sku: line.sku, ok })).catch(() => ({
            sku: line.sku,
            ok: false,
          }))
        )
      );
      for (const result of results) {
        if (result.ok) moved += 1;
        else skipped.push(result.sku);
      }
    }
  }

  const cartResponse = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  const cart = cartResponse.ok ? await cartResponse.json() : null;
  if (cart) {
    dispatchCartUpdate(cart);
  }

  return { moved, total: lines.length, skipped };
}

/**
 * Consume inbound `?PerC=` (or legacy `?cartjs=`) on page load, then strip it.
 */
export async function consumeCartJsParam() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get(PARAM) || params.get(LEGACY_PARAM);
  if (!code) return null;

  setCartIconRestoring(true);

  try {
    const lines = decodeCartCode(code);
    if (lines.length === 0) {
      stripCartJsParam();
      setCartIconRestoring(false);
      showNotice(t('cartjs_restored_none'), 'error');
      return { moved: 0, total: 0, skipped: [] };
    }

    const map = await loadSkuMap();
    const result = await restoreLines(lines, map);

    // Always clear the handoff param so it never sticks in the address bar.
    stripCartJsParam();

    if (result.moved === 0) {
      showNotice(t('cartjs_restored_none'), 'error');
    } else if (result.skipped.length > 0) {
      showNotice(t('cartjs_restored_partial', { moved: result.moved, total: result.total }), 'partial');
    } else {
      showNotice(t('cartjs_restored_all', { moved: result.moved, total: result.total }), 'success');
    }

    return result;
  } catch (error) {
    console.error('[cartjs] restore failed', error);
    stripCartJsParam();
    showNotice(t('cartjs_restore_error'), 'error');
    return null;
  } finally {
    setCartIconRestoring(false);
  }
}

/** Public API for Deliver To handoff. */
const CartJSApi = {
  encodeCartLines,
  decodeCartCode,
  cartToLines,
  buildShareUrl,
  buildShareUrlFromCart,
  readCartLines,
  consumeCartJsParam,
};

if (typeof window !== 'undefined') {
  window.CartJS = CartJSApi;

  const boot = () => {
    void consumeCartJsParam();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}

export { CartJSApi as CartJS };
